import { Image as ImageIcon, Upload } from 'lucide-react';
import type { ProductImageStyleCard } from './types';

export const PHOTO_STYLE_ID = 'reference-product-photo';

export function ReferenceImageStylePicker({ styleId, onChange, cards, loading, disabled, photo, photoUrl, onPhotoChange }: {
  styleId: string;
  onChange: (id: string) => void;
  cards: ProductImageStyleCard[];
  loading: boolean;
  disabled: boolean;
  photo: File | null;
  photoUrl: string | null;
  onPhotoChange: (file: File) => void;
}) {
  const usesPhoto = styleId === PHOTO_STYLE_ID;
  return <div className="space-y-4">
    <div>
      <h4 className="text-sm font-semibold text-text-primary">Źródło stylu zdjęć produktowych</h4>
      <p className="mt-1 text-xs text-text-muted">Wybierz jedno źródło. Zapisany wybór będzie używany przy generowaniu i regeneracji zdjęć. Możesz go później zmienić tutaj.</p>
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[
        { photo: false, label: 'Karta stylu', description: 'Styl z wybranej karty oraz plansza typografii.' },
        { photo: true, label: 'Własne zdjęcie referencyjne', description: 'Styl z wgranej grafiki, bez karty i planszy typografii.' },
      ].map(option => <button key={option.label} type="button" disabled={disabled} aria-pressed={usesPhoto === option.photo}
        onClick={() => onChange(option.photo ? PHOTO_STYLE_ID : cards[0]?.id || '')}
        className={`rounded-xl border p-4 text-left transition-colors disabled:opacity-50 ${usesPhoto === option.photo ? 'border-accent-primary bg-accent-primary/10' : 'border-border-subtle bg-surface-hover/30 hover:border-border-strong'}`}>
        <span className="text-sm font-semibold text-text-primary">{option.label}</span>
        <span className="mt-1 block text-xs text-text-muted">{option.description}</span>
      </button>)}
    </div>
    {usesPhoto ? <label className="block cursor-pointer rounded-xl border border-dashed border-accent-primary/30 bg-accent-primary/5 p-4 text-center"
      onDragOver={event => event.preventDefault()}
      onDrop={event => { event.preventDefault(); if (!disabled && event.dataTransfer.files[0]) onPhotoChange(event.dataTransfer.files[0]); }}>
      {photoUrl && !photo ? <img src={photoUrl} alt="Zapisana referencja stylu" className="mx-auto mb-3 h-32 object-contain" /> : <Upload className="mx-auto mb-3 h-6 w-6 text-accent-primary" />}
      <span className="block text-sm text-text-primary">{photo?.name || 'Przeciągnij zdjęcie lub wybierz plik stylu'}</span>
      <span className="mt-1 block text-xs text-text-muted">JPG, PNG lub WEBP, do 12 MB. Kopiujemy styl fotografii, produkt pochodzi z bazowych referencji.</span>
      <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Zdjęcie referencyjne stylu" className="sr-only" disabled={disabled}
        onChange={event => { const file = event.target.files?.[0]; event.currentTarget.value = ''; if (file) onPhotoChange(file); }} />
    </label> : loading ? <p className="text-xs text-text-muted">Wczytuję karty stylu...</p> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {cards.map(card => <button key={card.id} type="button" onClick={() => onChange(card.id)} disabled={disabled} aria-pressed={styleId === card.id}
        className={`overflow-hidden rounded-xl border text-left disabled:opacity-50 ${styleId === card.id ? 'border-accent-primary bg-accent-primary/10' : 'border-border-subtle bg-surface-hover/30'}`}>
        {card.imageUrl ? <img src={card.imageUrl} alt="" className="aspect-video w-full object-cover" /> : <ImageIcon className="mx-auto my-4 h-8 w-8 text-text-muted" />}
        <span className="block p-3 text-sm font-medium text-text-primary">{card.name}</span>
      </button>)}
      {!cards.length && <p className="text-xs text-text-muted">Brak aktywnych kart stylu. Wybierz własne zdjęcie referencyjne.</p>}
    </div>}
  </div>;
}
